  import React from 'react';
  import { Link, NavLink } from 'react-router-dom';
  import { motion } from 'framer-motion';
  import { Disclosure, Menu, Transition } from '@headlessui/react';
  import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

  // Product categories for dropdown
  const productCategories = [
    { name: 'Doshi Ayucare', path: '/products/doshi-ayucare' },
    { name: 'Whole Spices', path: '/products/whole-spices' },
    { name: 'Ground Spices', path: '/products/ground-spices' },
    { name: 'Spice Blends', path: '/products/spice-blends' },
    // { name: 'Organic Spices', path: '/products/organic-spices' },
    { name: 'Exotic Spices', path: '/products/exotic-spices' },
    
  ];

  const Header: React.FC = () => {
    const navigation = [
      { name: 'Home', path: '/' },
      { name: 'About', path: '/about' },
      { name: 'Products', path: '/products', dropdown: true },
      { name: 'Contact Us', path: '/contact' },
      { name: 'Certification', path: '/certification' },
    ];

    return (
      <Disclosure as="nav" className="bg-white shadow-md">
        {({ open }) => (
          <>
            <div className="container-custom">
              <div className="relative flex items-center justify-between h-20">
                <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                  {/* Mobile menu button */}
                  <Disclosure.Button className="inline-flex items-center justify-center p-2 rounded-md text-primary-500 hover:text-primary-800 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-spice-red">
                    <span className="sr-only">Open main menu</span>
                    {open ? (
                      <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                      <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                    )}
                  </Disclosure.Button>
                </div>
                
                {/* Logo */}
                <div className="flex-1 flex items-center justify-center sm:items-stretch sm:justify-start">
                  <motion.div 
                    className="flex-shrink-0 flex items-center"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Link to="/" className="flex items-center space-x-2">
                      <img 
                        src="/logo.png" 
                        alt="DD EXIM Logo"
                        className="h-10 sm:h-12 w-auto object-contain"
                      />
                    </Link>

                  </motion.div>
                  
                  {/* Desktop Navigation */}
                  <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                    {navigation.map((item) => 
                      !item.dropdown ? (
                        <NavLink
                          key={item.name}
                          to={item.path}
                          className={({ isActive }) => 
                            `inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                              isActive 
                                ? 'border-spice-red text-primary-900' 
                                : 'border-transparent text-primary-500 hover:border-primary-300 hover:text-primary-700'
                            }`
                          }
                        >
                          {item.name}
                        </NavLink>
                      ) : (
                        <Menu as="div" className="relative" key={item.name}>
                          <div>
                            <Menu.Button className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium text-primary-500 hover:border-primary-300 hover:text-primary-700">
                              {item.name}
                              <svg className="ml-1 -mr-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            </Menu.Button>
                          </div>
                          <Transition
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <Menu.Items className="absolute z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              <div className="py-1">
                                <Menu.Item>
                                  {({ active }) => (
                                    <Link
                                      to={item.path}
                                      className={`${
                                        active ? 'bg-primary-100 text-primary-900' : 'text-primary-700'
                                      } block px-4 py-2 text-sm font-medium`}
                                    >
                                      All Products
                                    </Link>
                                  )}
                                </Menu.Item>
                                <div className="border-t border-gray-200"></div>
                                {productCategories.map((category) => (
                                  <Menu.Item key={category.name}>
                                    {({ active }) => (
                                      <Link
                                        to={category.path}
                                        className={`${
                                          active ? 'bg-primary-100 text-primary-900' : 'text-primary-700'
                                        } block px-4 py-2 text-sm`}
                                      >
                                        {category.name}
                                      </Link>
                                    )}
                                  </Menu.Item>
                                ))}
                              </div>
                            </Menu.Items>
                          </Transition>
                        </Menu>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Navigation */}
            <Disclosure.Panel className="sm:hidden">
              <div className="pt-2 pb-3 space-y-1">
                {navigation.map((item) => 
                  !item.dropdown ? (
                    <NavLink
                      key={item.name}
                      to={item.path}
                      className={({ isActive }) => 
                        `block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                          isActive 
                            ? 'border-spice-red text-spice-red bg-primary-50' 
                            : 'border-transparent text-primary-500 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-700'
                        }`
                      }
                    >
                      {item.name}
                    </NavLink>
                  ) : (
                    <div key={item.name}>
                      <Disclosure>
                        {({ open }) => (
                          <>
                            <Disclosure.Button className="flex w-full pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-primary-500 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-700">
                              <span>{item.name}</span>
                              <svg 
                                className={`${open ? 'transform rotate-180' : ''} ml-auto h-5 w-5`} 
                                xmlns="http://www.w3.org/2000/svg" 
                                viewBox="0 0 20 20" 
                                fill="currentColor" 
                                aria-hidden="true"
                              >
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            </Disclosure.Button>
                            <Disclosure.Panel className="pl-6 pr-4 py-2 space-y-1">
                              <Link
                                to={item.path}
                                className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-primary-500 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-700"
                              >
                                All Products
                              </Link>
                              {productCategories.map((category) => (
                                <Link
                                  key={category.name}
                                  to={category.path}
                                  className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-primary-500 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-700"
                                >
                                  {category.name}
                                </Link>
                              ))}
                            </Disclosure.Panel>
                          </>
                        )}
                      </Disclosure>
                    </div>
                  )
                )}
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    );
  };

  export default Header; 